/**
 * Entry point. `trustedTypes` is imported first so its policy exists
 * before any other code runs.
 *
 * @module
 */
import "./trustedTypes";
import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { App } from "./App";
import { ApiError } from "./api/client";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // Auth failures are answers, not transient errors.
      retry: (count, err) =>
        !(err instanceof ApiError && err.status < 500) && count < 2,
      refetchOnWindowFocus: false,
    },
  },
});

// The server puts a fresh CSP nonce in this tag on every page load; Atlantic
// UI adds it to the <style> elements Emotion injects.
const nonce = document.querySelector<HTMLMetaElement>(
  'meta[name="csp-nonce"]',
)?.content;

const root = document.getElementById("root");
if (!root) throw new Error("missing #root");

createRoot(root).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App nonce={nonce} />
    </QueryClientProvider>
  </StrictMode>,
);
